import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

// Vast weekschema ("elke werkdag 9–12") naast de losse, flexibele
// beschikbaarheden. Een schema is één document in
// {basePath}/weeklySchedules: { weekdays: [1..5] (0 = zondag, JS-conventie),
// startTime: '09:00', endTime: '12:00', startDate: 'YYYY-MM-DD',
// endDate: 'YYYY-MM-DD' | null, timeZone: 'Europe/Brussels' }.
// Losse beschikbaarheden komen er bovenop (niet i.p.v.) — beide worden hier
// samengevoegd tot dezelfde vorm als een los availability-document, zodat
// bookingSlots.js en de agenda-overzichten niets van schema's hoeven te weten.
// Server-side kopie van de uitrol-logica: functions/src/lib/weeklySchedule.js
// — houd beide in lijn.

// Tot hoe ver vooruit een schema zonder einddatum boekbaar is.
export const SCHEDULE_HORIZON_DAYS = 180;

const formatterCache = {};
const getFormatter = (timeZone) => {
    if (!formatterCache[timeZone]) {
        formatterCache[timeZone] = new Intl.DateTimeFormat('en-US', {
            timeZone, hourCycle: 'h23',
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit'
        });
    }
    return formatterCache[timeZone];
};

const zonedParts = (date, timeZone) => {
    const map = {};
    getFormatter(timeZone).formatToParts(date).forEach(p => { map[p.type] = p.value; });
    return { year: +map.year, month: +map.month, day: +map.day, hour: +map.hour % 24, minute: +map.minute, second: +map.second };
};

// Offset (ms) van timeZone t.o.v. UTC op tijdstip ts.
const tzOffset = (ts, timeZone) => {
    const p = zonedParts(new Date(ts), timeZone);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(ts / 1000) * 1000;
};

// "09:00 op 2026-10-05 in Europe/Brussels" → absoluut Date-object, ook
// correct rond de overgang zomer-/wintertijd.
const wallTimeToDate = (y, m, d, hh, mm, timeZone) => {
    const guess = Date.UTC(y, m - 1, d, hh, mm);
    const offset = tzOffset(guess, timeZone);
    let ts = guess - offset;
    const offset2 = tzOffset(ts, timeZone);
    if (offset2 !== offset) ts = guess - offset2;
    return new Date(ts);
};

const ymdInZone = (date, timeZone) => {
    const p = zonedParts(date, timeZone);
    return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
};

// Rolt schema's uit tot concrete vensters tussen from en to (Date's).
export function expandWeeklySchedules(schedules, from, to) {
    const result = [];
    schedules.forEach(s => {
        if (!s.weekdays?.length || !s.startTime || !s.endTime) return;
        const timeZone = s.timeZone || 'Europe/Amsterdam';
        const [sh, sm] = s.startTime.split(':').map(Number);
        const [eh, em] = s.endTime.split(':').map(Number);
        if (eh * 60 + em <= sh * 60 + sm) return;

        let first = ymdInZone(from, timeZone);
        let last = ymdInZone(to, timeZone);
        if (s.startDate && s.startDate > first) first = s.startDate;
        if (s.endDate && s.endDate < last) last = s.endDate;
        if (first > last) return;

        const [fy, fm, fd] = first.split('-').map(Number);
        for (let cursor = new Date(Date.UTC(fy, fm - 1, fd)); ; cursor.setUTCDate(cursor.getUTCDate() + 1)) {
            const ymd = cursor.toISOString().slice(0, 10);
            if (ymd > last) break;
            if (!s.weekdays.includes(cursor.getUTCDay())) continue;
            const y = cursor.getUTCFullYear(), m = cursor.getUTCMonth() + 1, d = cursor.getUTCDate();
            const startTime = wallTimeToDate(y, m, d, sh, sm, timeZone);
            result.push({
                id: `${s.id}_${ymd}`,
                scheduleId: s.id,
                date: startTime,
                startTime,
                endTime: wallTimeToDate(y, m, d, eh, em, timeZone)
            });
        }
    });
    return result;
}

// Laadt losse beschikbaarheden + weekschema's van één agenda (basePath =
// 'users/{uid}' of 'users/{uid}/teamMembers/{id}') en geeft ze samen terug
// in de vorm { id, date, startTime, endTime } (Date-objecten).
export async function fetchCombinedAvailabilities(basePath) {
    const [availSnap, scheduleSnap] = await Promise.all([
        getDocs(collection(db, `${basePath}/availabilities`)),
        getDocs(collection(db, `${basePath}/weeklySchedules`))
    ]);

    const oneOffs = availSnap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        date: d.data().date.toDate(),
        startTime: d.data().startTime.toDate(),
        endTime: d.data().endTime.toDate()
    }));

    const from = new Date();
    from.setHours(0, 0, 0, 0);
    const to = new Date(from);
    to.setDate(to.getDate() + SCHEDULE_HORIZON_DAYS);
    const schedules = scheduleSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    return [...oneOffs, ...expandWeeklySchedules(schedules, from, to)]
        .sort((a, b) => a.startTime - b.startTime);
}
